import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/api";

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Attempt silent session restore
    (async () => {
      try {
        // Prefer regular user refresh first to avoid unnecessary staff checks
        try {
          const resp = await api.refresh();
          if (resp?.token) {
            setToken(resp.token);
            setUser(resp.user || null);
            setCompany(resp.company || null);
            setLoading(false);
            return;
          }
        } catch (_) {
          // No regular session, try staff session next
        }

        // Then try staff session restore
        try {
          const staffResp = await api.staffGetMe();
          if (staffResp?.staff) {
            const staffUser = {
              id: staffResp.staff.id,
              email: staffResp.staff.email,
              firstName: staffResp.staff.firstName,
              lastName: staffResp.staff.lastName,
              role: 'STAFF',
            };
            const systemCompany = { id: 'system', name: 'System Administration' };
            setToken(null);
            setUser(staffUser);
            setCompany(systemCompany);
            setLoading(false);
            return;
          }
        } catch (_) {
          // no staff session either
        }
      } catch (_) {
        // no active session
      } finally {
        setLoading(false);
      }
    })();
    
    const logoutHandler = (e) => {
      setToken(null);
      setUser(null);
      setCompany(null);
    };
    const staffLoginHandler = (e) => {
      const s = (e && e.detail) || null;
      if (!s) return;
      const staffUser = {
        id: s.id,
        email: s.email,
        firstName: s.firstName,
        lastName: s.lastName,
        role: 'STAFF',
      };
      const systemCompany = { id: 'system', name: 'System Administration' };
      setToken(null);
      setUser(staffUser);
      setCompany(systemCompany);
    };
    
    window.addEventListener('auth:logout', logoutHandler);
    window.addEventListener('staff:login', staffLoginHandler);
    return () => {
      window.removeEventListener('auth:logout', logoutHandler);
      window.removeEventListener('staff:login', staffLoginHandler);
    };
  }, []);

  const login = async (email, password, rememberMe = true, twoFactorToken = null) => {
    const payload = { email, password, rememberMe };
    if (twoFactorToken) {
      payload.twoFactorToken = twoFactorToken;
    }
    
    const res = await api.login(payload);
    
    // Check if 2FA is required (for staff users)
    if (res.requiresTwoFactor) {
      return { requiresTwoFactor: true, method: res.method || 'TOTP' };
    }
    
    // Check if staff user (uses separate session system)
    if (res.isStaff && res.staff) {
      // Use staff data directly from login response (cookie is already set)
      const staffUser = {
        id: res.staff.id,
        email: res.staff.email,
        firstName: res.staff.firstName,
        lastName: res.staff.lastName,
        role: 'STAFF',
      };
      const systemCompany = {
        id: 'system',
        name: 'System Administration',
      };
      setUser(staffUser);
      setCompany(systemCompany);
      return { user: staffUser, company: systemCompany };
    }
    
    // Regular user
    setToken(res.token);
    setUser(res.user);
    setCompany(res.company);
    return { token: res.token, user: res.user, company: res.company };
  };

  const logout = async () => {
    try {
      // Check if staff user
      if (user?.role === 'STAFF') {
        await api.staffLogout();
      } else {
        await api.logout();
      }
    } catch(_) {}
    setToken(null);
    setUser(null);
    setCompany(null);
  };

  const setStaffOrgAccess = (accessToken, user, company) => {
    setToken(accessToken);
    setUser(user);
    setCompany(company);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        company,
        login,
        logout,
        loading,
        setCompany, // expose for UI refresh after updates
        setStaffOrgAccess, // for staff organization switching
        isAuthenticated: !!token || !!user, // Staff users don't have token, check user instead
        isStaff: user?.role === 'STAFF', // computed property instead of separate state
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
